/*
 * Copyright 2019 VMware, Inc.
 * SPDX-License-Identifier: EPL-2.0
 */
import { Injectable } from "@angular/core";
import { EventEmitter } from "@angular/core";
import { LocaleService } from "./locale.service";
import { I18nResource } from "./i18n.resource";
import { I18nLoader } from "../loader";
import { assign, deprecatedWarn } from "../util";
import { concat, Observable, forkJoin, Observer, of, Subscription, BehaviorSubject, ReplaySubject } from "rxjs";
import { share, switchMap, take, catchError, map } from "rxjs/operators";
import { VIPConfig, VIPChildConfig, getNameSpace, VIPConfigFactory, PatternCategories } from "../config";
import { sourceBundleObject } from "./l10n.service";
import { I18nContext } from "./i18n.context";
import { ContentData } from "./content.data";

export interface LocaleData {
    categories: Object;
    messages: { [key: string]: any };
}

export interface LoadingLocaleData {
    locale: string;
    data: Observable<any>;
}

export interface NamespaceConfig {
    [key: string]: VIPConfig;
}

export interface VIPError {
    code: VIPErrorCode;
    data: VIPErrorData;
    error: any;
}

export enum VIPErrorCode {
    ComponentLoadFailure,
}

export type VIPErrorData = ComponentLoadFailureErrorData;

export interface ComponentLoadFailureErrorData {
    componentNamespace: string;
}

@Injectable()
export class VIPService {
    public mainConfig: VIPConfig;
    private pending = false;
    private namespaceConfig: NamespaceConfig = {};
    private loadingLocaleData: LoadingLocaleData;
    private localeSubscription: Subscription;
    private _onLocaleChange: EventEmitter<string> = new EventEmitter<string>();
    private errorStream = new VipErrorStream();
    private _i18nScope: Array<PatternCategories> = [];

    constructor(
        protected localeService: LocaleService,
        protected i18nResource: I18nResource,
        protected i18nContext: I18nContext,
        public currentLoader: I18nLoader
    ) {}

    get onLocaleChange(): EventEmitter<string> {
        return this._onLocaleChange;
    }

    get i18nScope() {
        return this._i18nScope;
    }

    /**
     * Public error stream that applications can listen to get all errors
     */
    public get errors$(): Observable<VIPError> {
        return this.errorStream.errors$;
    }

    /**
     * Public error stream that applications can listen to get the latest error
     */
    public get lastError$(): Observable<VIPError> {
        return this.errorStream.lastError$;
    }

    /**
     * Loading the i18n data at app module or root of lazy module.
     * @param config For root module or lazy load module.
     */
    public async initData(config: VIPConfig): Promise<any> {
        this.initI18nContext(config);
        this.subscribeLocaleSubject();
        this.registerComponent(config, true);
        this.initLocale(config);
        await this.loadLocaleData();
    }

    /**
     * Load the i18n data in lazy load module.
     * @param childConfig For lazy load module.
     */
    public async initLazyModuleData(childConfig: VIPChildConfig): Promise<any> {
        const config = this.resolveConfiguration(childConfig);
        this.subscribeLocaleSubject();
        this.registerComponent(config, true);
        this.initLocale(config);
        await this.loadLocaleData();
    }

    /**
     * Add 'productID, version, host' from root module config as default values to I18nContext.
     * @param config VIPConfig
     */
    private initI18nContext(config: VIPConfig) {
        if (!this.i18nContext.productID) {
            if (config.host) {
                this.i18nContext.host = config.host;
            }
            this.i18nContext.productID = config.productID;
            this.i18nContext.version = config.version;
        }
    }

    /**
     * If the configuration from child module is missing the required field
     * use the default value from the root module configuration to fill in.
     * @param childConfig configuration from child module
     * @returns A standard VIP I18n configuration
     */
    public resolveConfiguration(childConfig: VIPChildConfig): VIPConfig {
        if (childConfig && !childConfig.productID) {
            childConfig.productID = this.i18nContext.productID;
        }
        if (childConfig && !childConfig.version) {
            childConfig.version = this.i18nContext.version;
        }
        if (childConfig && !childConfig.host) {
            childConfig.host = this.i18nContext.host ? this.i18nContext.host : undefined;
        }
        return <VIPConfig>childConfig;
    }

    /**
     * Init locale by config passed from root module for compatibility.
     * Will be removed in 13.
     * @param config
     */
    private initLocale(config: VIPConfig) {
        if (config.locale || config.language || config.region) {
            console.warn(
                "Set language and region by configuration is deprecated, will be removed in v13. " +
                    "Please use LocaleService.init instead."
            );
            if (config.language && config.region) {
                this.localeService.init(config.language, config.region);
            } else if (config.locale) {
                this.localeService.init(config.locale);
            }
        }
    }

    /**
     * Subscrible the locale changed event from view layer.
     * For live UI translation update.
     */
    public subscribeLocaleSubject() {
        if (!this.localeSubscription) {
            this.localeSubscription = this.localeService.userLocaleChanged.subscribe(() => {
                this.loadLocaleData();
            });
        }
    }

    /**
     * Specifically, for the feature module which will be imported
     * to root module or lazy load module. This method should be invoked
     * in the constructor of feature module.
     * @param config VIP configuration includes all required information.
     * @param isMain Indicates if it's root module or isolated feature module.
     * Lazy module is also considered as main module.
     */
    public registerComponent(config: VIPConfig, isMain: boolean = false) {
        this.subscribeLocaleSubject();
        config = VIPConfigFactory(config, this.i18nContext);
        this.mainConfig = isMain ? config : this.mainConfig;

        // Collect and update all required pattern categories.
        this.updateI18nScope(config);

        // In theory, the configuration of the same namespace should be the same,
        // but for compatibility with previous versions, any configuration file that comes in,
        // first extracts the corresponding source bundles to avoid any source missing.
        if (config.sourceBundles || config.sourceBundle) {
            if (config.sourceBundle) {
                deprecatedWarn("The sourceBundle option in i18n config", "v13", "sourceBundles");
            }
            const bundle = config.sourceBundles ? this.resetBundle(config.sourceBundles) : config.sourceBundle;
            this.processBundle(bundle, this.localeService.defaultLocale.languageCode, config);
        }

        // Build a one-to-one mapping.
        const namespace = getNameSpace(config);
        this.namespaceConfig[namespace] = config;

        // Register shared translation namespace.
        if (!config.isolated) {
            this.i18nResource.registerSharedNamespace(namespace);
        }
    }

    /**
     * Tentative solution, if translation bundle is mounted.
     * no longer to consider i18n scope which defined in
     * feature module configuration.
     * @param config The VIP configuration object.
     */
    private updateI18nScope(config: VIPConfig) {
        if (config.i18nScope && !config.translationBundles) {
            this._i18nScope = this._i18nScope.concat(config.i18nScope).filter((elem, index, self) => {
                return index === self.indexOf(elem);
            });
        }
    }

    /**
     * Load missing translations and patterns for all registered configurations.
     */
    public async loadLocaleData(): Promise<any> {
        const language = this.localeService.getCurrentLanguage();
        const region = this.localeService.getCurrentRegion();
        const locale = this.localeService.getCurrentLocale();
        if (!this.localeService.isSourceLocale && this.i18nContext.i18nEnabled !== false) {
            await this.getLocaleData(language, region, locale).toPromise();
        }
        this.releaseLocale(locale);
    }

    private getLocaleData(language: string, region: string, locale: string): Observable<any> {
        const sequencesOfRequest: Array<Observable<any>> = [];
        const namespaceList = Object.keys(this.namespaceConfig);
        let loadingLocaleData: Observable<any>;
        let restI18nScope = [...this._i18nScope];

        // Start traversing all registered configuration files,
        // to check if there are any resources that need to be downloaded.
        for (let namespace of namespaceList) {
            const config = this.namespaceConfig[namespace];
            let filteredScope = [];

            // Check whether the requested pattern data already exists,
            // and if not, load missing pattern data.
            filteredScope = this.i18nResource.filterScope(locale, config.i18nScope).filter((elem) => {
                const index = restI18nScope.indexOf(elem);
                if (index >= 0) restI18nScope.splice(index, 1);
                return index >= 0;
            });

            // If there is no pattern data to load, reset the scope.
            config.i18nScope = filteredScope.length > 0 ? filteredScope : [];

            // Check if translations for the current language exists in the corresponding namespace.
            const isTranslationExist = this.i18nResource.isTanslationExist(namespace, locale);

            // Bypass sending HTTP call to get translation.
            // Using local bundle instead.
            // Using language to map local bundle.
            if (config.translationBundles) {
                const standardLanguageTag = this.localeService.normalizeLanguageCode(language);
                const bundle = config.translationBundles[language] || config.translationBundles[standardLanguageTag];
                if (bundle) {
                    this.processBundle(bundle, locale, config);
                }
            } else {
                // Only when translation or pattern missing, send data request.
                if (!isTranslationExist || config.i18nScope.length > 0) {
                    sequencesOfRequest.push(
                        this.currentLoader.getLocaleData(config, language, region).pipe(
                            share(),
                            catchError((err) => {
                                console.error(`Can't fetch locale data.`, config, err);
                                const componentNamespace = getNameSpace(config);
                                this.errorStream.next({
                                    code: VIPErrorCode.ComponentLoadFailure,
                                    data: { componentNamespace },
                                    error: err,
                                });
                                return of(undefined);
                            })
                        )
                    );
                }
            }
        }

        // No HTTP call required.
        // Resource of current locale is available.
        if (sequencesOfRequest.length < 1) {
            return of([]);
        }

        loadingLocaleData = forkJoin(sequencesOfRequest);
        this.loadingLocaleData = {
            data: loadingLocaleData.pipe(take(1), share()),
            locale: locale,
        };

        // FIFO
        this.pending = true;
        this.loadingLocaleData.data.subscribe({
            next: (res: LocaleData) => {
                this.storeResource(res, locale);
                this.pending = false;
            },
            error: (err: any) => {
                this.pending = false;
            },
        });
        return loadingLocaleData;
    }

    private resetBundle(sourceBundles: any) {
        if (Array.isArray(sourceBundles)) {
            return assign({}, sourceBundles);
        }
        return undefined;
    }

    private processBundle(bundle: any, locale: string, config: VIPConfig) {
        const formattedBundle = { messages: {} };
        const nameSpace = getNameSpace(config);
        (formattedBundle.messages as any)[nameSpace] = bundle;
        this.storeResource([formattedBundle], locale);
    }

    private storeResource(res: any, locale: string) {
        if (res) {
            res.forEach((element: any) => {
                if (element) {
                    if (element.messages) {
                        // Using nameSpace and locale to store the translation.
                        const nameSpace = Object.keys(element.messages)[0];
                        this.i18nResource.addTranslation(nameSpace, locale, element.messages[nameSpace]);
                    }
                    if (element.categories) {
                        this.i18nResource.addPattern(locale, element.categories);
                    }
                }
            });
        }
    }

    /**
     * Current represents current available locale to cover the initial status.
     * since in some situation, the 'onLocaleChange' haven't be register always,
     * or there is no event at time.
     */
    get current(): Observable<string | any> {
        if (this.pending) {
            const locale = this.loadingLocaleData.locale;
            return new Observable((observer: Observer<string>) => {
                const onComplete = () => {
                    observer.next(locale);
                    observer.complete();
                };
                this.loadingLocaleData.data.subscribe((res: LocaleData) => {
                    onComplete();
                }, onComplete);
            });
        } else {
            return of(this.localeService.getCurrentLocale());
        }
    }

    /**
     * Stream of 'available' locale.
     */
    get stream(): Observable<string | any> {
        return concat(
            this.current,
            this.onLocaleChange.pipe(
                switchMap((locale: string) => {
                    return of(locale);
                })
            )
        );
    }

    private releaseLocale(locale: string) {
        this.onLocaleChange.emit(locale);
    }

    /**
     * Register source bundle to the given configuration on-demand.
     * @param sourceBundles The source bundle object list to be mounted.
     * @param config The target configuration for the specific product and component.
     */
    public registerSourceBundles(sourceBundles: sourceBundleObject[], config: VIPConfig) {
        if (sourceBundles) {
            const bundle = this.resetBundle(sourceBundles);
            this.processBundle(bundle, this.localeService.defaultLocale.languageCode, config);
        }
    }

    /**
     * Load the content through the pre-defined key.
     * @param config Contains all required information of the application.
     * @param tplKey The key of the content template to get.
     * @returns An observable that, when subscribed, load the content from the server.
     */
    public loadContent(config: VIPConfig, tplKey: string): Observable<ContentData> {
        const fixedConfig = VIPConfigFactory(config, this.i18nContext);
        let contentData = new ContentData(fixedConfig, tplKey);
        return !this.localeService.isSourceLocale && this.i18nContext.i18nEnabled !== false
            ? this.loadLocalizedContent(fixedConfig, contentData)
            : this.loadSourceContent(fixedConfig, contentData);
    }

    private loadContentFromCache(config: VIPConfig, tplKey: string, locale: string): ContentData | undefined {
        const namespace = getNameSpace(config);
        const translation = this.i18nResource.getTranslation(namespace, locale);
        const cache = translation && translation[tplKey];
        return cache instanceof ContentData ? cache : undefined;
    }

    private loadSourceContent(config: VIPConfig, contentData: ContentData): Observable<ContentData> {
        const locale = this.localeService.defaultLocale.languageCode;
        const content = this.loadContentFromCache(config, contentData.tplKey, locale);
        if (content) return of(content);

        return this.currentLoader.getAsset(contentData.srcPath).pipe(
            map((source: string) => {
                contentData.tplValue = source;
                this.storeContent(locale, config, { [contentData.tplKey]: contentData });
                return contentData;
            }),
            catchError((err) => {
                console.error(err);
                return of(contentData);
            })
        );
    }

    private loadLocalizedContent(config: VIPConfig, contentData: ContentData): Observable<ContentData> {
        const locale = this.localeService.getCurrentLocale();
        const content = this.loadContentFromCache(config, contentData.tplKey, locale);
        if (content) return of(content);

        return this.currentLoader.getTranslationByKeys(config, contentData.extractKeys(), locale).pipe(
            map((translations: Object) => {
                contentData.tplValue = translations && translations[contentData.tplKey];
                if (!contentData.tplValue) {
                    throw new Error(`Content template not found for the key: ${contentData.tplKey}.`);
                }

                const storeData: { [key: string]: ContentData } = {};
                storeData[contentData.tplKey] = new ContentData(config, contentData.tplKey);

                Object.keys(translations)
                    .filter((key) => key != contentData.tplKey && contentData.assets[key.trim()])
                    .forEach((key) => {
                        contentData.assets[key.trim()]["value"] = translations[key];
                        storeData[contentData.tplKey].assets[key.trim()]["value"] = translations[key];
                    });

                this.storeContent(locale, config, storeData);
                return contentData;
            }),
            catchError((err) => {
                console.error(err);
                return this.loadSourceContent(config, contentData);
            })
        );
    }

    private storeContent(locale: string, config: VIPConfig, storeData: { [key: string]: ContentData }) {
        const namespace = getNameSpace(config);
        this.i18nResource.addTranslation(namespace, locale, storeData);
    }
}

class VipErrorStream {
    private allErrors = new ReplaySubject<VIPError>();
    private lastError = new BehaviorSubject<VIPError>(null);

    next(err: VIPError) {
        this.allErrors.next(err);
        this.lastError.next(err);
    }

    get errors$(): Observable<VIPError> {
        return this.allErrors.asObservable();
    }

    get lastError$(): Observable<VIPError> {
        return this.lastError.asObservable();
    }
}
